/* Copyright (c) 2020-present CloudIO Inc */

import { AnyItem, ItemType, Page } from '../../registry/ComponentRegistry';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionOnCancel';
import { getManager } from '../../registry/Manager';

const ActionOnCancelManager: ActionManagerFn<'ActionOnCancel'> = (
  platform: Platform,
): ActionManager<'ActionOnCancel'> => {
  return {
    ...baseActionManager(platform, 'ActionOnCancel'),
    isChildAllowed: (page, item, childType) =>
      getManager(childType).managerType === 'Action',
    defaultValue,
    props: {
      ...baseActionProps,
    },
    name: 'On Cancel',
    icon: { iconName: 'times' },
    description: `**Overview**<br/>
The "On Cancel" action is a specialized logic container designed solely to handle the "negative" outcome of a User Confirmation dialog. It acts as the "Else" branch for the confirmation workflow, executing a defined sequence of child actions only when the user clicks the "Cancel" or "No" button in the confirmation pop-up.<br/><br/>
This component is structurally dependent on the ActionUserConfirmation component and cannot exist independently. It serves as a wrapper for other actions, allowing developers to define what should happen if the user decides not to proceed with a critical operation.

**Key Features**<br/>
* **Strict Parent Scope:** Explicitly restricted via isAllowedInsideParent to function only as a child of ActionUserConfirmation. It will not appear or function correctly if placed elsewhere.
* **Logic Grouping:** Functions as a generic container that allows any standard Action component to be added as a child (isChildAllowed).
* **Event Handling:** Automatically triggered by the "Cancel" event of the parent confirmation window.

**Properties**<br/>
* **Base Properties:** This component utilizes ...baseActionProps exclusively, accepting only the standard configuration options available to all actions.

**Use Cases**<br/>
* **Feedback:** Showing a "Operation Cancelled" toast message using ActionShowMessage when the user declines a delete request.
* **State Reset:** Resetting a toggle switch or checkbox back to its previous state if the user cancels the confirmation dialog associated with changing it.`,
    isAllowedInsideParent: (page: Page, parent: AnyItem) => {
      return (['ActionUserConfirmation'] as ItemType[]).includes(parent.type);
    },
  };
};

export default ActionOnCancelManager;
